import type { Cache, Variables } from '@urql/exchange-graphcache';

/** Invalidate every cached arg-set of a Query field (optionally filtered by args). */
export const invalidateQueryField = (cache: Cache, fieldName: string, filter?: (args: Variables | null) => boolean) => {
  for (const field of cache.inspectFields('Query')) {
    if (field.fieldName !== fieldName) continue;
    if (filter && !filter(field.arguments)) continue;
    cache.invalidate('Query', fieldName, field.arguments ?? undefined);
  }
};

/** Invalidate every cached arg-set of several Query fields in a single field scan. */
export const invalidateQueryFields = (cache: Cache, fieldNames: string[]) => {
  const wanted = new Set(fieldNames);
  for (const field of cache.inspectFields('Query')) {
    if (wanted.has(field.fieldName)) {
      cache.invalidate('Query', field.fieldName, field.arguments ?? undefined);
    }
  }
};

export const invalidateBookingById = (cache: Cache, bookingId: string) => {
  cache.invalidate({ __typename: 'Booking', _id: bookingId });
};

const invalidateCreditActivityForBooking = (cache: Cache, bookingId: string) => {
  invalidateQueryField(cache, 'creditActivityByBooking', args => args?._booking_id === bookingId);
};

export const invalidateBookingAndCredits = (cache: Cache, bookingId: string) => {
  invalidateBookingById(cache, bookingId);
  invalidateCreditActivityForBooking(cache, bookingId);
};

// Invalidate `findings` queries and each booking's `processing_status_counts`
// in one field scan rather than walking Query twice.
export const invalidateResultsAggregations = (cache: Cache) => {
  for (const field of cache.inspectFields('Query')) {
    if (field.fieldName === 'findings') {
      cache.invalidate('Query', 'findings', field.arguments ?? undefined);
    } else if (field.fieldName === 'bookingByID') {
      const bookingKey = cache.resolve('Query', 'bookingByID', field.arguments ?? undefined);
      if (typeof bookingKey === 'string') {
        cache.invalidate(bookingKey, 'processing_status_counts');
      }
    }
  }
};

/**
 * The removal mutations answer with just the ids they touched, and the totals and aggregates that
 * counted those responses are computed server-side, so every affected read has to come back fresh.
 */
export const invalidateRemovedByoSubmissions = (cache: Cache, submissionIds: readonly string[]) => {
  for (const submissionId of submissionIds) {
    cache.invalidate({ __typename: 'Submission', _id: submissionId });
  }
  invalidateQueryFields(cache, ['bookingTaskResults', 'bookingTaskParticipants', 'bookingSubmissionConnection']);
  invalidateResultsAggregations(cache);
};
